/**
 * POFormDialog — ultra-detailed New/Edit Purchase Order form.
 * Header + repeatable line items with MCI search, autofill, and live totals.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  X, Save, Plus, Trash2, Loader2, ClipboardCheck, Search, Package, DollarSign,
  Building2, FileText, ChevronDown, ChevronRight, Truck,
} from 'lucide-react';

const now = () => new Date().toLocaleString();
const currentUser = () => {
  try { return JSON.parse(localStorage.getItem('ciq_user') || '{}'); }
  catch { return {}; }
};
const emptyLine = (n) => ({
  poLineItem: `L${String(n).padStart(2, '0')}`, mciLineNumber: '', materialCode: '',
  materialDescription: '', quantity: '', unit: '', unitPrice: '', lineValue: '',
  requiredAtSiteDate: '', notes: '',
});
const EMPTY_PO = {
  poNumber: '', poRevision: 'R0', poDate: new Date().toISOString().slice(0, 10),
  vendor: '', vendorId: null, buyerEntity: '', buyerContactEmail: '',
  vendorContactEmail: '', currency: 'USD', incoterms: '', paymentTerms: '',
  deliveryAddress: '', warrantyMonths: '', ldClausePercent: '', advancePercent: '',
  mrNumber: '', vendorDrawingRef: '', inspectionPlanRef: '', freightTerms: '',
  poDocumentRef: '', lineItems: [emptyLine(1)], status: 'DRAFT', notes: '',
};

const inp = "w-full px-2 py-1.5 rounded border text-xs";
const inpSty = { background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' };
const lbl = "text-[9px] uppercase font-semibold block mb-1 tracking-wider";
const lblSty = { color: 'var(--text-muted)' };

function Section({ icon: Icon, title, open, onToggle, children, testId }) {
  return (
    <div className="rounded-lg border" style={{ borderColor: 'var(--border-color)' }} data-testid={testId}>
      <button type="button" onClick={onToggle} className="w-full px-3 py-2 flex items-center gap-2 text-left" style={{ background: 'var(--card-bg-alt)' }}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Icon className="w-4 h-4" style={{ color: 'var(--tone-navy-text)' }} />
        <span className="text-[11px] uppercase font-bold tracking-wider" style={{ color: 'var(--text-primary)' }}>{title}</span>
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

function VendorPicker({ projectId, value, onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mci/vendors?q=${encodeURIComponent(q)}&limit=15`);
        setItems(data.vendors || []);
      } catch { setItems([]); }
      setBusy(false);
    }, 250);
    return () => clearTimeout(t);
  }, [projectId, q]);

  return (
    <div className="relative" data-testid="po-vendor-picker">
      <div className="flex gap-1">
        <input value={value || q} onChange={e => { setQ(e.target.value); onPick({ vendor: e.target.value, contactEmail: null, id: null }); }}
          placeholder="Search vendor master…" className={inp} style={inpSty} data-testid="po-vendor-input" />
        <Search className="w-4 h-4 -ml-6 mt-2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
      </div>
      {q.length >= 1 && items.length > 0 && (
        <div className="absolute z-20 left-0 right-0 bg-[var(--card-bg)] border rounded shadow-lg mt-1 max-h-60 overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
          {items.map(v => {
            const primary = (v.contacts || []).find(c => c.role === 'PRIMARY') || (v.contacts || [])[0];
            return (
              <button key={v.id} type="button" onClick={() => {
                onPick({ vendor: v.legalName, id: v.id, contactEmail: primary?.email || '',
                          currency: v.defaultCurrency, incoterms: v.defaultIncoterms,
                          paymentTerms: v.defaultPaymentTerms });
                setQ('');
              }}
                className="block w-full text-left px-2 py-1 text-[11px] hover:bg-slate-50"
                data-testid={`po-vendor-opt-${v.id}`}>
                <div className="font-semibold" style={{ color: 'var(--tone-navy-text)' }}>{v.legalName}</div>
                <div style={{ color: 'var(--text-muted)' }}>
                  {v.country || '—'} · {primary?.name || 'no PoC'} · {primary?.email || ''}
                </div>
              </button>
            );
          })}
          {busy && <div className="p-2 text-center text-[10px]" style={{ color: 'var(--text-muted)' }}>searching…</div>}
        </div>
      )}
    </div>
  );
}

function MciLinePicker({ projectId, value, onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const inputVal = value || q;
  useEffect(() => {
    if (!q || q.length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mci/entries?search=${encodeURIComponent(q)}&limit=15`);
        setItems(data.entries || data || []);
      } catch { setItems([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [projectId, q]);
  return (
    <div className="relative">
      <input value={inputVal} onChange={e => { setQ(e.target.value); onPick({ mciLineNumber: e.target.value }); }}
        placeholder="Search MCI…" className={inp} style={inpSty} />
      {q.length >= 2 && items.length > 0 && (
        <div className="absolute z-20 left-0 right-0 bg-[var(--card-bg)] border rounded shadow-lg mt-1 max-h-56 overflow-y-auto" style={{ borderColor: 'var(--border-color)' }}>
          {items.map(m => (
            <button key={m.mciLineNumber} type="button" onClick={() => {
              onPick({
                mciLineNumber: m.mciLineNumber,
                materialCode: m.tagNumber || m.bulkItemCode || '',
                materialDescription: m.materialDescription || '',
                unit: m.unit || '',
                quantity: m.quantity || '',
              });
              setQ('');
            }}
              className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50">
              <div className="font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>{m.mciLineNumber}</div>
              <div className="truncate" style={{ color: 'var(--text-primary)' }}>{m.materialDescription}</div>
              <div style={{ color: 'var(--text-muted)' }}>{m.discipline || '—'} · {m.unit || '—'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function POFormDialog({ projectId, po, onClose, onSaved }) {
  const editing = !!po?.id;
  const [form, setForm] = useState(po ? { ...EMPTY_PO, ...po } : EMPTY_PO);
  const [lookups, setLookups] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({ header: true, commercial: true, refs: true, lines: true });
  const user = useMemo(currentUser, []);

  useEffect(() => {
    let alive = true;
    api.get(`/v1/projects/${projectId}/mci/vendor-po/lookups`)
      .then(r => { if (alive) setLookups(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [projectId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, patch) => setForm(f => ({
    ...f, lineItems: f.lineItems.map((li, idx) => {
      if (idx !== i) return li;
      const next = { ...li, ...patch };
      const q = parseFloat(next.quantity), p = parseFloat(next.unitPrice);
      if (!isNaN(q) && !isNaN(p)) next.lineValue = (q * p).toFixed(2);
      return next;
    })
  }));
  const addLine = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, emptyLine(f.lineItems.length + 1)] }));
  const rmLine = (i) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, idx) => idx !== i) }));

  const total = useMemo(() =>
    (form.lineItems || []).reduce((s, li) => s + (parseFloat(li.lineValue) || 0), 0),
    [form.lineItems]);

  const submit = async () => {
    if (!form.poNumber.trim()) return toast.error('PO Number required');
    if (!form.vendor.trim()) return toast.error('Vendor required');
    if (!form.lineItems.length) return toast.error('At least one line item required');
    setSaving(true);
    try {
      const clean = { ...form,
        warrantyMonths: form.warrantyMonths ? parseInt(form.warrantyMonths) : null,
        ldClausePercent: form.ldClausePercent ? parseFloat(form.ldClausePercent) : null,
        advancePercent: form.advancePercent ? parseFloat(form.advancePercent) : null,
        lineItems: form.lineItems.map(li => ({
          ...li,
          quantity: li.quantity ? parseFloat(li.quantity) : 0,
          unitPrice: li.unitPrice ? parseFloat(li.unitPrice) : null,
          lineValue: li.lineValue ? parseFloat(li.lineValue) : null,
        })),
      };
      if (editing) {
        await api.put(`/v1/projects/${projectId}/mci/pos/${encodeURIComponent(po.poNumber)}`, clean);
        toast.success(`PO ${clean.poNumber} updated`);
      } else {
        await api.post(`/v1/projects/${projectId}/mci/pos`, clean);
        toast.success(`PO ${clean.poNumber} created`);
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };
  const L = lookups || { currencies: ['USD'], incoterms: [], paymentTerms: [], poStatuses: ['DRAFT','APPROVED','ISSUED'] };
  const toggle = k => setOpen(o => ({ ...o, [k]: !o[k] }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose} data-testid="po-form-dialog">
      <div className="w-full max-w-5xl h-[92vh] rounded-xl shadow-2xl flex flex-col"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
        onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" style={{ color: '#F26B1F' }} />
            <div>
              <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                {editing ? `Edit PO · ${po.poNumber}` : 'New Purchase Order'}
              </div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {editing ? 'Updated' : 'Raised'} by <b>{user.name || user.email || 'current user'}</b> · {now()}
                {editing && po?.createdBy && <> · Originally by <b>{po.createdBy}</b></>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[9px] uppercase" style={{ color: 'var(--text-muted)' }}>Total Value</div>
              <div className="text-lg font-bold font-mono" style={{ color: '#F26B1F' }}>
                {form.currency} {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <Section icon={FileText} title="PO Header" open={open.header} onToggle={() => toggle('header')} testId="section-po-header">
            <div className="grid grid-cols-4 gap-2">
              <div><label className={lbl} style={lblSty}>PO Number *</label>
                <input value={form.poNumber} onChange={e => set('poNumber', e.target.value)} className={inp} style={inpSty} data-testid="po-number" disabled={editing} /></div>
              <div><label className={lbl} style={lblSty}>Revision</label>
                <input value={form.poRevision} onChange={e => set('poRevision', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>PO Date *</label>
                <input type="date" value={form.poDate} onChange={e => set('poDate', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)} className={inp} style={inpSty} data-testid="po-status">
                  {L.poStatuses.map(s => <option key={s}>{s}</option>)}
                </select></div>
              <div className="col-span-2"><label className={lbl} style={lblSty}>Vendor * (search master)</label>
                <VendorPicker projectId={projectId} value={form.vendor}
                  onPick={p => setForm(f => ({ ...f, vendor: p.vendor, vendorId: p.id, vendorContactEmail: p.contactEmail || f.vendorContactEmail,
                    currency: p.currency || f.currency, incoterms: p.incoterms || f.incoterms, paymentTerms: p.paymentTerms || f.paymentTerms }))} /></div>
              <div><label className={lbl} style={lblSty}>Buyer Entity</label>
                <input value={form.buyerEntity || ''} onChange={e => set('buyerEntity', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Buyer Contact Email</label>
                <input value={form.buyerContactEmail || user.email || ''} onChange={e => set('buyerContactEmail', e.target.value)} className={inp} style={inpSty} /></div>
              <div className="col-span-2"><label className={lbl} style={lblSty}>Vendor Contact Email (auto-filled)</label>
                <input value={form.vendorContactEmail || ''} onChange={e => set('vendorContactEmail', e.target.value)} className={inp} style={inpSty} data-testid="po-vendor-email" /></div>
              <div><label className={lbl} style={lblSty}>MR / Requisition #</label>
                <input value={form.mrNumber || ''} onChange={e => set('mrNumber', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>PO Document Ref (URL)</label>
                <input value={form.poDocumentRef || ''} onChange={e => set('poDocumentRef', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={DollarSign} title="Commercial Terms" open={open.commercial} onToggle={() => toggle('commercial')} testId="section-po-commercial">
            <div className="grid grid-cols-4 gap-2">
              <div><label className={lbl} style={lblSty}>Currency</label>
                <select value={form.currency} onChange={e => set('currency', e.target.value)} className={inp} style={inpSty}>
                  {L.currencies.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Incoterms</label>
                <select value={form.incoterms || ''} onChange={e => set('incoterms', e.target.value)} className={inp} style={inpSty}>
                  <option value="">—</option>{L.incoterms.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div className="col-span-2"><label className={lbl} style={lblSty}>Payment Terms</label>
                <select value={form.paymentTerms || ''} onChange={e => set('paymentTerms', e.target.value)} className={inp} style={inpSty}>
                  <option value="">—</option>{L.paymentTerms.map(c => <option key={c}>{c}</option>)}
                </select></div>
              <div><label className={lbl} style={lblSty}>Warranty (months)</label>
                <input type="number" value={form.warrantyMonths || ''} onChange={e => set('warrantyMonths', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>LD Clause (%)</label>
                <input type="number" value={form.ldClausePercent || ''} onChange={e => set('ldClausePercent', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Advance (%)</label>
                <input type="number" value={form.advancePercent || ''} onChange={e => set('advancePercent', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Freight Terms</label>
                <input value={form.freightTerms || ''} onChange={e => set('freightTerms', e.target.value)} className={inp} style={inpSty} /></div>
              <div className="col-span-4"><label className={lbl} style={lblSty}>Delivery Address</label>
                <input value={form.deliveryAddress || ''} onChange={e => set('deliveryAddress', e.target.value)} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={FileText} title="References" open={open.refs} onToggle={() => toggle('refs')} testId="section-po-refs">
            <div className="grid grid-cols-2 gap-2">
              <div><label className={lbl} style={lblSty}>Vendor Drawing Ref</label>
                <input value={form.vendorDrawingRef || ''} onChange={e => set('vendorDrawingRef', e.target.value)} className={inp} style={inpSty} /></div>
              <div><label className={lbl} style={lblSty}>Inspection Plan Ref</label>
                <input value={form.inspectionPlanRef || ''} onChange={e => set('inspectionPlanRef', e.target.value)} className={inp} style={inpSty} /></div>
              <div className="col-span-2"><label className={lbl} style={lblSty}>Notes</label>
                <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className={inp} style={inpSty} /></div>
            </div>
          </Section>

          <Section icon={Package} title={`Line Items (${form.lineItems.length})`} open={open.lines} onToggle={() => toggle('lines')} testId="section-po-lines">
            <div className="rounded border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
              <table className="w-full text-[10px]">
                <thead style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="p-1 text-left" style={lblSty}>Line</th>
                    <th className="p-1 text-left" style={lblSty}>MCI (search)</th>
                    <th className="p-1 text-left" style={lblSty}>Material Code</th>
                    <th className="p-1 text-left" style={lblSty}>Description</th>
                    <th className="p-1 text-right" style={lblSty}>Qty</th>
                    <th className="p-1 text-left" style={lblSty}>Unit</th>
                    <th className="p-1 text-right" style={lblSty}>Unit Price</th>
                    <th className="p-1 text-right" style={lblSty}>Line Value</th>
                    <th className="p-1 text-left" style={lblSty}>RAS</th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.lineItems.map((li, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`po-line-row-${i}`}>
                      <td className="p-1"><input value={li.poLineItem} onChange={e => setLine(i, { poLineItem: e.target.value })} className={inp} style={{ ...inpSty, minWidth: 50 }} /></td>
                      <td className="p-1" style={{ minWidth: 160 }}>
                        <MciLinePicker projectId={projectId} value={li.mciLineNumber} onPick={p => setLine(i, p)} />
                      </td>
                      <td className="p-1"><input value={li.materialCode || ''} onChange={e => setLine(i, { materialCode: e.target.value })} className={inp} style={inpSty} /></td>
                      <td className="p-1" style={{ minWidth: 220 }}>
                        <input value={li.materialDescription || ''} onChange={e => setLine(i, { materialDescription: e.target.value })} className={inp} style={inpSty} /></td>
                      <td className="p-1"><input type="number" value={li.quantity} onChange={e => setLine(i, { quantity: e.target.value })} className={inp} style={{ ...inpSty, textAlign: 'right' }} /></td>
                      <td className="p-1"><input value={li.unit || ''} onChange={e => setLine(i, { unit: e.target.value })} className={inp} style={inpSty} /></td>
                      <td className="p-1"><input type="number" value={li.unitPrice} onChange={e => setLine(i, { unitPrice: e.target.value })} className={inp} style={{ ...inpSty, textAlign: 'right' }} /></td>
                      <td className="p-1 text-right font-mono font-semibold" style={{ color: '#F26B1F' }}>
                        {li.lineValue ? Number(li.lineValue).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                      </td>
                      <td className="p-1"><input type="date" value={li.requiredAtSiteDate || ''} onChange={e => setLine(i, { requiredAtSiteDate: e.target.value })} className={inp} style={inpSty} /></td>
                      <td className="p-1"><button type="button" onClick={() => rmLine(i)} className="px-1 rounded" style={{ color: '#EF4444' }}><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="add-po-line-btn"><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
          </Section>
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2 sticky bottom-0" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="po-save"
            className="text-white font-semibold" style={{ background: '#F26B1F' }}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {editing ? 'Update PO' : 'Create PO'}
          </Button>
        </div>
      </div>
    </div>
  );
}
